import { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import {
  Sun, Moon, Menu, X,
  Mail as MailIcon, ExternalLink, Check as CheckIcon,
  MapPin, GraduationCap, Briefcase, Globe, FileText
} from "lucide-react";
import "./index.css";

gsap.registerPlugin(ScrollTrigger);

const PIPES = [
  "M -100 150 L 300 150 L 300 450 L 700 450 L 700 800 L 1200 800 L 1200 1200",
  "M 1500 100 L 1200 100 L 1200 600 L 800 600 L 800 1200 L 300 1200",
  "M -100 800 L 200 800 L 200 600 L 500 600 L 500 200 L 1000 200 L 1000 -100",
  "M 1600 900 L 1400 900 L 1400 400 L 1000 400 L 1000 800 L 500 800 L 500 1300",
  "M 800 -100 L 800 300 L 400 300 L 400 900 L 0 900",
];

function GlowingPipes() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30 md:opacity-50">
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" className="absolute top-0 left-0 w-full h-[150vh] min-h-[1200px] min-w-[1500px]">
        {PIPES.map((d, i) => (
          <g key={i}>
            <path d={d} stroke="currentColor" strokeOpacity="0.05" strokeWidth="2" fill="none" />
            <path 
              d={d} 
              className="text-primary stroke-current"
              strokeWidth="2" 
              fill="none" 
              strokeDasharray="150 1500" 
              style={{
                animation: `pipePulse ${15 + i * 3}s linear infinite`,
                animationDelay: `-${i * 5}s`,
                filter: 'drop-shadow(0 0 6px currentColor)'
              }} 
            />
          </g>
        ))}
      </svg>
    </div>
  );
}

// ── DATA ──────────────────────────────────────────────────────────────────
const PROJECTS = [
  { id: 1, title: "DocuSense", desc: "Built a RAG application for chatting with PDF documents and scraped web pages. Uses Groq-hosted open-source LLMs, local HuggingFace embeddings, and MongoDB Atlas Vector Search to deliver context-aware insights.", tags: ["React", "Vite", "Python", "FastAPI", "HuggingFace", "MongoDB Atlas", "Groq", "BeautifulSoup", "Playwright"], color: "#6366f1", year: "2026", link: "https://docu-sense-beta.vercel.app/", github: "https://github.com/himanshu2871/DocuSense" },
  { id: 2, title: "Newsly", desc: "Built a full-stack AI-powered news aggregator using React and Node.js. Features personalized topic feeds, AI-generated article summaries via Groq API, JWT auth, PostgreSQL database, and a curated Daily News Brief.", tags: ["React", "Node.js", "Express", "PostgreSQL", "Tailwind CSS", "JavaScript"], color: "#06b6d4", year: "2025", link: "https://newsly-peach.vercel.app/", github: "https://github.com/himanshu2871/newsly" },
];

const SKILLS = [
  { cat: "Frontend", color: "#6366f1", items: ["React", "JavaScript", "Tailwind CSS", "GSAP"] },
  { cat: "Backend", color: "#06b6d4", items: ["Node.js", "Express", "Python", "FastAPI", "GraphQL"] },
  { cat: "Database", color: "#10b981", items: ["PostgreSQL", "MongoDB"] },
  { cat: "DevOps", color: "#f59e0b", items: ["Docker", "GitHub Actions", "Vercel"] },
];

const ROLES = ["Full-Stack Developer", "React Developer", "API Architect", "Tech Enthusiast"];
const NAV_LINKS = ["Home", "About", "Projects", "Skills", "Contact"];
const RESUME_LINK = "https://drive.google.com/file/d/1o6IAVsFdovDZ5xqijB8yUdonuXCG28zC/view?usp=drive_link";

// ── HOOKS ─────────────────────────────────────────────────────────────────
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const fn = e => setMatches(e.matches);
    mq.addEventListener("change", fn);
    setMatches(mq.matches);
    return () => mq.removeEventListener("change", fn);
  }, [query]);
  return matches;
}

function useTypewriter(texts, speed = 75, pause = 2000) {
  const [display, setDisplay] = useState("");
  const [idx, setIdx] = useState(0);
  const [char, setChar] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    const cur = texts[idx];
    const t = setTimeout(() => {
      if (!del && char < cur.length) { setChar(c => c + 1); setDisplay(cur.slice(0, char + 1)); }
      else if (!del) { setTimeout(() => setDel(true), pause); }
      else if (del && char > 0) { setChar(c => c - 1); setDisplay(cur.slice(0, char - 1)); }
      else { setDel(false); setIdx(i => (i + 1) % texts.length); }
    }, del ? speed / 2 : speed);
    return () => clearTimeout(t);
  }, [char, del, idx, texts, speed, pause]);
  return display;
}

// ── ICONS ─────────────────────────────────────────────────────────────────
const GH = ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.6.113.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" /></svg>;
const LI = ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>;

// ── COMPONENTS ────────────────────────────────────────────────────────────
function SectionHeader({ title, icon }) {
  return (
    <div className="flex items-center gap-5 mb-12">
      <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight whitespace-nowrap flex items-center gap-4">
        {title}
        {icon && <span className="text-primary">{icon}</span>}
      </h2>
      <div className="flex-1 h-px bg-gradient-to-r from-base-content/20 to-transparent" />
    </div>
  );
}

function ProjectCard({ p, hoveredSkill }) {
  const isDimmed = hoveredSkill && !p.tags.includes(hoveredSkill);
  return (
    <div
      className={`card bg-base-200/60 backdrop-blur-xl border-2 transition-all duration-300 overflow-hidden group 
        ${isDimmed ? 'opacity-30 scale-95 border-transparent' : 'border-base-300 hover:border-primary hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/30'}`}
    >
      <div className="card-body p-7">
        <div className="flex justify-between items-start mb-4">
          <div className="w-12 h-12 rounded-2xl bg-base-300 flex items-center justify-center font-extrabold" style={{ color: p.color, border: `1px solid ${p.color}40` }}>
            0{p.id}
          </div>
          <div className="flex gap-3 items-center opacity-70 group-hover:opacity-100 transition-opacity text-base-content">
            <span className="text-xs font-semibold">{p.year}</span>
            {p.link && <a href={p.link} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors"><ExternalLink size={15}/></a>}
            {p.github && <a href={p.github} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors"><GH s={15} /></a>}
          </div>
        </div>
        <h3 className="card-title text-xl font-bold tracking-tight mb-2">{p.title}</h3>
        <p className="text-sm opacity-75 leading-relaxed mb-5">{p.desc}</p>
        <div className="flex flex-wrap gap-2 mt-auto">
          {p.tags.map(tag => (
            <span key={tag} className="px-3 py-1 rounded-lg text-xs font-semibold" style={{ color: p.color, border: `1px solid ${p.color}40`, backgroundColor: `${p.color}14` }}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function FlashlightCursor({ theme }) {
  const cursorRef = useRef(null);
  
  useGSAP(() => {
    let xTo = gsap.quickTo(cursorRef.current, "x", {duration: 0.1, ease: "power3"}),
        yTo = gsap.quickTo(cursorRef.current, "y", {duration: 0.1, ease: "power3"});
    const handleMove = (e) => { xTo(e.clientX); yTo(e.clientY); };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);
  
  // Hide on mobile or small screens 
  const isMobile = window.innerWidth < 768;
  if(isMobile) return null;

  return (
    <div ref={cursorRef} className="fixed top-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none z-[9999] opacity-30 transform -translate-x-1/2 -translate-y-1/2 mix-blend-screen" 
         style={{ background: `radial-gradient(circle, ${theme === "night" ? "rgba(255,121,198,0.15)" : "rgba(56,189,248,0.15)"}, transparent 50%)` }} />
  );
}

// ── APP ───────────────────────────────────────────────────────────────────
export default function App() {
  const [theme, setTheme] = useState("night");
  const [active, setActive] = useState("Home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  
  const mainRef = useRef(null);
  const role = useTypewriter(ROLES);

  const isMobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => { if (!isMobile) setMenuOpen(false); }, [isMobile]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // GSAP Animations
  useGSAP(() => {
    // Reveal sections on scroll
    gsap.utils.toArray('.gsap-reveal').forEach(section => {
      gsap.from(section, {
        scrollTrigger: {
          trigger: section,
          start: "top 85%",
        },
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out"
      });
    });

    // Hero Timeline
    const tl = gsap.timeline();
    tl.from(".hero-badge", { y: -20, opacity: 0, duration: 0.5, ease: "back.out(1.7)", delay: 0.2 })
      .from(".hero-title", { y: 30, opacity: 0, duration: 0.7, ease: "power3.out" }, "-=0.3")
      .from(".hero-role", { opacity: 0, duration: 0.5 }, "-=0.4")
      .from(".hero-desc", { y: 20, opacity: 0, duration: 0.5 }, "-=0.3")
      .from(".hero-actions", { y: 20, opacity: 0, duration: 0.4 }, "-=0.3")
      .from(".hero-stack", { y: 12, opacity: 0, duration: 0.4 }, "-=0.2");

    // Parallax Orbs
    gsap.to(".bg-orb-1", {
      yPercent: 30, xPercent: 10,
      ease: "none",
      scrollTrigger: { trigger: document.body, start: "top top", end: "bottom top", scrub: 1 }
    });
    gsap.to(".bg-orb-2", {
      yPercent: -20, xPercent: -10,
      ease: "none",
      scrollTrigger: { trigger: document.body, start: "top top", end: "bottom top", scrub: 1 }
    });
  }, { scope: mainRef, dependencies: [] });

  useEffect(() => {
    const fn = () => {
      let currentActive = NAV_LINKS[0];
      for (let i = 0; i < NAV_LINKS.length; i++) {
        const el = document.getElementById(NAV_LINKS[i]);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 || (i === 0 && rect.top <= window.innerHeight)) {
            currentActive = NAV_LINKS[i];
          }
        }
      }
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 10) {
        currentActive = NAV_LINKS[NAV_LINKS.length - 1];
      }
      setActive(currentActive);
    };
    window.addEventListener("scroll", fn);
    fn();
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = id => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setActive(id);
    setMenuOpen(false);
  };

  return (
    <div ref={mainRef} className="min-h-screen bg-base-100 text-base-content overflow-x-hidden font-sans transition-colors duration-300">
      <FlashlightCursor theme={theme} />

      {/* Ambient background orbs & pipes */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <GlowingPipes />
        <div className="bg-orb-1 absolute w-[clamp(500px,80vw,1100px)] h-[clamp(500px,80vw,1100px)] rounded-full -top-[15%] -left-[10%] bg-primary/5 blur-[100px]" />
        <div className="bg-orb-2 absolute w-[clamp(400px,70vw,900px)] h-[clamp(400px,70vw,900px)] rounded-full top-[25%] -right-[10%] bg-secondary/5 blur-[100px]" />
      </div>

      {/* ─── NAVBAR ─── */}
      <nav className="navbar fixed top-0 w-full z-50 bg-base-100/70 backdrop-blur-xl border-b border-base-200/50 px-4 md:px-12 h-[68px]">
        <div className="flex-1">
          <button onClick={() => go("Home")} className="btn btn-ghost normal-case text-xl font-bold tracking-tight group">
            <span className="text-primary opacity-70 group-hover:opacity-100 transition-opacity mr-1 font-mono">~</span>
            <span className="group-hover:text-primary transition-colors">/himanshu</span>
            <span className="text-secondary opacity-80 animate-pulse ml-1 font-mono">_</span>
          </button>
        </div>
        <div className="flex-none hidden md:flex gap-2">
          {NAV_LINKS.map(l => (
            <button key={l} onClick={() => go(l)} className={`btn btn-sm rounded-full ${active === l ? 'border-primary text-primary bg-primary/10 hover:bg-primary/20' : 'btn-ghost text-base-content/70'}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex-none flex items-center gap-2 ml-4">
          <button onClick={() => setTheme(t => t === "night" ? "pastel" : "night")} className="btn btn-sm btn-ghost btn-circle">
            {theme === "night" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {isMobile && (
            <button onClick={() => setMenuOpen(o => !o)} className="btn btn-sm btn-ghost btn-circle">
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}
        </div>
      </nav>

      {isMobile && (
        <div className={`fixed top-[68px] left-0 w-full z-40 bg-base-100/95 backdrop-blur-xl border-b border-base-200 shadow-xl transition-all duration-300 overflow-hidden ${menuOpen ? 'max-h-96 py-4' : 'max-h-0 py-0'}`}>
          <div className="flex flex-col gap-2 px-6">
            {NAV_LINKS.map(l => (
              <button key={l} onClick={() => go(l)} className={`btn rounded-full justify-start ${active === l ? 'btn-primary' : 'btn-ghost'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── MAIN CONTENT ─── */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12">
        
        {/* ─── HERO ─── */}
        <section id="Home" className="min-h-[100vh] flex flex-col justify-center pt-24 pb-16">
          <div className="hero-badge mb-6 py-3 px-4 font-bold shadow-sm rounded-full w-max flex items-center" 
               style={{ color: '#00e676', border: '1px solid rgba(0,230,118,0.5)', backgroundColor: 'rgba(0,230,118,0.1)' }}>
            <span className="w-2 h-2 rounded-full mr-2 animate-pulse" style={{ backgroundColor: '#00e676' }} /> 
            Available for new projects
          </div>
          <div className="hero-title text-base-content/70 font-medium mb-2 text-lg">Hi there, I'm</div>
          <h1 className="hero-title text-6xl md:text-8xl font-black tracking-tighter mb-4 pb-2">
            <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">Himanshu.</span>
          </h1>
          <div className="hero-role font-mono text-2xl md:text-3xl text-base-content/80 mb-6 font-medium">
            {role}<span className="text-primary animate-pulse">|</span>
          </div>
          <p className="hero-desc text-lg md:text-xl text-base-content/70 max-w-2xl leading-relaxed mb-10">
            I engineer robust, end-to-end web applications. Passionate about architecting scalable backends, developing seamless user interfaces, and turning complex problems into elegant full-stack solutions.
          </p>
          <div className="hero-actions flex flex-wrap items-center gap-4">
            <button onClick={() => go("Projects")} className="btn btn-primary btn-lg rounded-2xl shadow-lg hover:-translate-y-1 transition-transform">
              View My Work →
            </button>
            <a href={RESUME_LINK} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-lg rounded-2xl backdrop-blur-sm border-base-content/20 hover:-translate-y-1 transition-transform gap-2">
              <FileText size={20} /> Resume
            </a>
            <button onClick={() => go("Contact")} className="btn btn-ghost btn-lg rounded-2xl hover:-translate-y-1 transition-transform">
              Get In Touch ✉
            </button>
          </div>
          <div className="hero-stack mt-8 flex flex-wrap items-center gap-2.5" aria-label="Core technologies">
            <span className="mr-1 font-mono text-xs font-bold tracking-widest text-base-content/45">CORE STACK</span>
            {["React", "Node.js", "FastAPI", "PostgreSQL", "Docker"].map(tech => (
              <span key={tech} className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-base-content/75">
                {tech}
              </span>
            ))}
          </div>
        </section>

        {/* ─── ABOUT ─── */}
        <section id="About" className="pb-24 pt-10 gsap-reveal">
          <SectionHeader title="About Me" />
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 items-start">
            <div className="card bg-base-200/50 backdrop-blur-md border border-base-content/5 shadow-xl p-8 text-center">
              <div className="avatar mx-auto mb-6">
                <div className="w-32 rounded-full ring ring-primary ring-offset-base-100 ring-offset-4 shadow-xl">
                  <img src="/IMG_20260127_200458_795.jpg" alt="Himanshu Sharma" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-1">Himanshu Sharma</h3>
              <p className="text-base-content/60 mb-6">Full-Stack Developer · India</p>
              <div className="flex justify-center gap-4">
                {[["GitHub", "https://github.com/himanshu2871", <GH />], ["LinkedIn", "https://www.linkedin.com/in/himanshu-sharma-i9b7/", <LI />], ["Resume", RESUME_LINK, <FileText size={16}/>], ["Email", "mailto:purusharma916@gmail.com", <MailIcon size={16}/>]].map(([label, href, icon]) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="btn btn-circle btn-ghost border border-base-content/10 hover:border-primary hover:text-primary" title={label}>
                    {icon}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="text-lg text-base-content/80 leading-relaxed mb-6">
                Hey! I'm a passionate full-stack developer who loves building things that live on the internet. I specialize in creating modern, performant web applications using React, Node.js, and cloud infrastructure.
              </p>
              <p className="text-lg text-base-content/80 leading-relaxed mb-10">
                When I'm not coding, I'm exploring new technologies, learning about software development. I'm always open to interesting projects and collaborations.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[[<MapPin size={20} />, "Location", "Bengaluru"], [<GraduationCap size={20} />, "Education", "B.E. CS"], [<Briefcase size={20} />, "Experience", "1 Year"], [<Globe size={20} />, "Languages", "English, Hindi"]].map(([icon, label, value]) => (
                  <div key={label} className="card bg-base-200/60 p-4 border border-base-content/5 items-start text-left">
                    <div className="text-primary mb-2">{icon}</div>
                    <div className="text-xs uppercase tracking-wider font-bold opacity-50 mb-1">{label}</div>
                    <div className="font-semibold">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ─── PROJECTS ─── */}
        <section id="Projects" className="pb-24 pt-10 gsap-reveal">
          <SectionHeader title="Projects" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {PROJECTS.map(p => <ProjectCard key={p.id} p={p} hoveredSkill={hoveredSkill} />)}
          </div>
        </section>

        {/* ─── SKILLS ─── */}
        <section id="Skills" className="pb-24 pt-10 gsap-reveal">
          <SectionHeader title="Skills & Tech" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SKILLS.map(({ cat, color, items }) => (
              <div key={cat} className="card bg-base-200/50 backdrop-blur-md border border-base-content/5 p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full shadow-[0_0_10px_currentColor]" style={{ backgroundColor: color, color: color }} />
                  <span className="font-bold text-lg" style={{ color }}>{cat}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {items.map(item => (
                    <span key={item} 
                      onMouseEnter={() => setHoveredSkill(item)}
                      onMouseLeave={() => setHoveredSkill(null)}
                      className={`badge badge-lg border-opacity-40 transition-all duration-300 font-medium ${hoveredSkill === item ? 'scale-110 shadow-md' : 'opacity-80'}`}
                      style={{ 
                        color, 
                        borderColor: color, 
                        backgroundColor: hoveredSkill === item ? `${color}25` : `${color}10` 
                      }}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── CONTACT ─── */}
        <section id="Contact" className="pb-32 pt-10 gsap-reveal">
          <SectionHeader title="Get In Touch" icon={<MailIcon size={38} />} />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2 card bg-base-200/40 p-8 border border-base-content/5 shadow-xl backdrop-blur-md">
              <h3 className="text-3xl font-bold mb-4">
                Let's build something <span className="text-primary font-black">amazing.</span>
              </h3>
              <p className="text-base-content/70 leading-relaxed mb-8">
                Open to full-time roles, freelance work, and interesting collaborations. I typically respond within 24 hours.
              </p>
              <div className="space-y-6">
                {[["Email", "purusharma916@gmail.com", "mailto:purusharma916@gmail.com", <MailIcon/>], ["GitHub", "github.com/himanshu2871", "https://github.com/himanshu2871", <GH/>], ["LinkedIn", "linkedin.com/in/himanshu-sharma-i9b7", "https://www.linkedin.com/in/himanshu-sharma-i9b7/", <LI/>]].map(([label, val, href, icon]) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-content transition-colors">
                      {icon}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest font-bold opacity-50">{label}</div>
                      <div className="font-medium group-hover:text-primary transition-colors">{val}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
            <div className="lg:col-span-3 card bg-base-200/60 p-8 shrink-0 shadow-2xl border border-base-content/5 backdrop-blur-xl rounded-[2rem]">
              {sent ? (
                <div className="h-full flex flex-col items-center justify-center gap-4 py-10">
                  <div className="text-success w-16 h-16 rounded-full border-2 border-success flex items-center justify-center animate-bounce">
                    <CheckIcon size={32} strokeWidth={3}/>
                  </div>
                  <h3 className="text-2xl font-bold text-success">Message Sent!</h3>
                  <p className="opacity-70 max-w-xs text-center mb-4">Thanks for reaching out. I'll reply within 24 hours.</p>
                  <button onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }} className="btn btn-outline">
                    Send Another
                  </button>
                </div>
              ) : (
                <form onSubmit={e => { e.preventDefault(); setSending(true); setTimeout(() => { setSending(false); setSent(true); }, 1500); }} className="space-y-4">
                  <h3 className="text-xl font-bold mb-2">Send a Message</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-control">
                      <input type="text" placeholder="Your full name" required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="input input-bordered input-primary bg-base-100 rounded-full" />
                    </div>
                    <div className="form-control">
                      <input type="email" placeholder="your@email.com" required value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="input input-bordered input-primary bg-base-100 rounded-full" />
                    </div>
                  </div>
                  <div className="form-control">
                    <textarea placeholder="Tell me about your project or idea..." required rows={5} value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} className="textarea textarea-bordered textarea-primary bg-base-100 text-base rounded-[2rem]" />
                  </div>
                  <button type="submit" disabled={sending} className="btn btn-primary w-full rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all mt-2">
                    {sending ? <span className="loading loading-spinner"></span> : "Send Message →"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* ─── FOOTER ─── */}
      <footer className="footer items-center p-8 bg-base-200 text-base-content border-t border-base-content/10 relative z-10 w-full flex flex-wrap justify-between">
        <aside className="items-center grid-flow-col gap-3">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-primary-content font-black shadow-lg">H</div>
          <p className="font-semibold text-base">
            Crafting digital <span className="text-primary">experiences.</span>
          </p>
        </aside>
        <div className="text-sm opacity-60">
          Designed & built by Himanshu · {new Date().getFullYear()}
        </div>
        <nav className="grid-flow-col gap-4 md:place-self-center md:justify-self-end">
          {[["GitHub", "https://github.com/himanshu2871", <GH />], ["LinkedIn", "https://www.linkedin.com/in/himanshu-sharma-i9b7/", <LI />], ["Email", "mailto:purusharma916@gmail.com", <MailIcon />]].map(([label, href, icon]) => (
             <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 hover:text-primary transition-colors">
               {icon}
             </a>
          ))}
        </nav>
      </footer>
    </div>
  );
}
